const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;
const I18N = window.helpSearchI18n || {};

function t(key, fallback, replacement = null) {
    const value = I18N[key] || fallback;

    if (replacement === null) {
        return value;
    }

    return value.replace('%s', replacement);
}

class HelpSearch {
    constructor(container) {
        this.container = container;
        this.form = container.querySelector('.js-help-search-form');
        this.input = container.querySelector('.js-help-search-input');
        this.resultsContainer = document.querySelector('.js-help-search-results');
        this.searchInfo = document.querySelector('.js-help-search-info');
        this.categorySlug = this.container.dataset.category || null;
        this.categoryName = this.container.dataset.categoryName || null;
        this.archiveUrl = this.container.dataset.archiveUrl || '';
        this.apiBase = `${window.location.origin}/wp-json/ausdauersport/v1`;
        this.debounceTimer = null;
        this.suggestionsEl = null;
        this.activeSuggestionIndex = -1;
        this.suggestions = [];

        this.buildSuggestions();
        this.bindEvents();
    }

    buildSuggestions() {
        this.suggestionsEl = document.createElement('div');
        this.suggestionsEl.className = 'help-search__suggestions';
        this.suggestionsEl.hidden = true;
        this.form.appendChild(this.suggestionsEl);
    }

    bindEvents() {
        this.input.addEventListener('input', () => this.onInput());
        this.input.addEventListener('keydown', (e) => this.onKeydown(e));
        this.form.addEventListener('submit', (e) => this.onSubmit(e));
        document.addEventListener('click', (e) => {
            if (!this.form.contains(e.target)) {
                this.hideSuggestions();
            }
        });

        this.input.addEventListener('focus', () => {
            if (this.suggestions.length > 0) {
                this.suggestionsEl.hidden = false;
            }
        });

        const removeBtn = this.container.querySelector('.js-help-search-category-remove');
        if (removeBtn) {
            removeBtn.addEventListener('click', (e) => this.onCategoryRemove(e));
        }
    }

    onInput() {
        clearTimeout(this.debounceTimer);
        const query = this.input.value.trim();

        if (query.length < MIN_CHARS) {
            this.hideSuggestions();
            return;
        }

        this.debounceTimer = setTimeout(() => this.fetchSuggestions(query), DEBOUNCE_MS);
    }

    onKeydown(e) {
        if (this.suggestionsEl.hidden) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.activeSuggestionIndex = Math.min(this.activeSuggestionIndex + 1, this.suggestions.length - 1);
            this.highlightSuggestion();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.activeSuggestionIndex = Math.max(this.activeSuggestionIndex - 1, -1);
            this.highlightSuggestion();
        } else if (e.key === 'Enter' && this.activeSuggestionIndex >= 0) {
            e.preventDefault();
            const selected = this.suggestions[this.activeSuggestionIndex];
            if (selected) {
                window.location.href = selected.url;
            }
        } else if (e.key === 'Escape') {
            this.hideSuggestions();
        }
    }

    onSubmit(e) {
        e.preventDefault();
        const query = this.input.value.trim();
        if (!query) return;

        this.hideSuggestions();
        this.search(query);
    }

    onCategoryRemove(e) {
        e.preventDefault();
        this.categorySlug = null;
        this.categoryName = null;

        const tag = this.container.querySelector('.help-search__category-tag');
        if (tag) tag.remove();

        this.form.action = this.archiveUrl;
        this.container.dataset.category = '';

        const query = this.input.value.trim();
        if (query) {
            this.search(query);
        }

        this.updateUrl(query);
    }

    async fetchSuggestions(query) {
        const params = new URLSearchParams({search: query});
        if (this.categorySlug) {
            params.set('category', this.categorySlug);
        }

        try {
            const res = await fetch(`${this.apiBase}/help-pages?${params}`);
            const data = await res.json();
            this.suggestions = (data.pages || []).slice(0, 6);
            this.renderSuggestions(query);
        } catch {
            this.hideSuggestions();
        }
    }

    renderSuggestions(query) {
        if (this.suggestions.length === 0) {
            this.hideSuggestions();
            return;
        }

        this.activeSuggestionIndex = -1;
        this.suggestionsEl.innerHTML = this.suggestions.map((page, i) => {
            const categoryLabel = page.categories.length > 0 ? page.categories[0].name : '';
            return `<a href="${page.url}" class="help-search__suggestion" data-index="${i}">
                <span class="help-search__suggestion-title">${this.highlightMatch(page.title, query)}</span>
                ${categoryLabel ? `<span class="help-search__suggestion-category">${categoryLabel}</span>` : ''}
            </a>`;
        }).join('');

        const allLink = document.createElement('button');
        allLink.type = 'submit';
        allLink.className = 'help-search__suggestion help-search__suggestion--all';
        allLink.textContent = t('showAllResultsFor', 'Alle Ergebnisse für "%s" anzeigen', query);
        this.suggestionsEl.appendChild(allLink);

        this.suggestionsEl.hidden = false;

        this.suggestionsEl.querySelectorAll('.help-search__suggestion').forEach((el) => {
            el.addEventListener('mouseenter', () => {
                this.activeSuggestionIndex = parseInt(el.dataset.index, 10) || -1;
                this.highlightSuggestion();
            });
        });
    }

    highlightMatch(text, query) {
        const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return text.replace(new RegExp(`(${escaped})`, 'gi'), '<strong>$1</strong>');
    }

    highlightSuggestion() {
        this.suggestionsEl.querySelectorAll('.help-search__suggestion').forEach((el, i) => {
            el.classList.toggle('is-active', i === this.activeSuggestionIndex);
        });
    }

    hideSuggestions() {
        this.suggestionsEl.hidden = true;
        this.activeSuggestionIndex = -1;
        this.suggestions = [];
    }

    async search(query) {
        if (!this.resultsContainer) return;

        const params = new URLSearchParams({search: query});
        if (this.categorySlug) {
            params.set('category', this.categorySlug);
        }

        this.resultsContainer.innerHTML = `<p class="help-content__loading">${t('searchLoading', 'Suche...')}</p>`;
        this.updateSearchInfo(query);
        this.updateUrl(query);

        try {
            const res = await fetch(`${this.apiBase}/help-pages?${params}`);
            const data = await res.json();
            this.renderResults(data.pages || []);
        } catch {
            this.resultsContainer.innerHTML = `<p class="help-content__no-results">${t('searchError', 'Fehler bei der Suche.')}</p>`;
        }
    }

    renderResults(pages) {
        if (pages.length === 0) {
            this.resultsContainer.innerHTML = `<p class="help-content__no-results">${t('noResults', 'Keine Ergebnisse gefunden.')}</p>`;
            return;
        }

        this.resultsContainer.innerHTML = `<div class="news-tab-teaser__grid">
            ${pages.map((page) => `<a class="slider-teaser-object swiper-slide slider-teaser-object--no-image" href="${page.url}">
                <div class="slider-teaser-object__body">
                    <p class="slider-teaser-object__headline headline headline--h3">${page.title}</p>
                    ${page.excerpt ? `<div class="slider-teaser-object__teaser">${page.excerpt}</div>` : ''}
                    <div class="slider-teaser-object__button button button--secondary">${t('learnMore', 'Mehr erfahren')}</div>
                </div>
            </a>`).join('')}
        </div>`;
    }

    updateSearchInfo(query) {
        if (!this.searchInfo) return;

        if (query) {
            this.searchInfo.innerHTML = `${t('searchResultsFor', 'Suchergebnisse für')}: <strong>"${query}"</strong>
                <a href="#" class="help-content__reset-link js-help-search-reset">${t('resetSearch', 'Suche zurücksetzen')}</a>`;
            this.searchInfo.hidden = false;

            const resetLink = this.searchInfo.querySelector('.js-help-search-reset');
            if (resetLink) {
                resetLink.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.resetSearch();
                });
            }
        } else {
            this.searchInfo.hidden = true;
        }
    }

    resetSearch() {
        this.input.value = '';
        this.hideSuggestions();

        if (this.categorySlug) {
            window.location.href = this.container.dataset.categoryUrl || window.location.pathname;
        } else {
            window.location.href = this.archiveUrl;
        }
    }

    updateUrl(query) {
        const url = new URL(window.location);
        if (query) {
            url.searchParams.set('hilfe_suche', query);
        } else {
            url.searchParams.delete('hilfe_suche');
        }

        if (!this.categorySlug && this.archiveUrl) {
            const archiveUrlObj = new URL(this.archiveUrl);
            url.pathname = archiveUrlObj.pathname;
        }

        window.history.pushState({}, '', url);
    }
}

const helpSearch = {
    init() {
        const containers = document.querySelectorAll('.js-help-search');
        containers.forEach((container) => new HelpSearch(container));
    },
};

export default helpSearch;
